import {UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {Grid, Typography} from "@mui/material";
import {Frame} from "../base/Frame";
import {LabeledInput} from "../base/LabeledInput";
import {LanguageType} from "../../types/domain/languageType";
import {MeasureSystemType} from "../../types/domain/measureSystemType";
import {CheckboxInput} from "../base/CheckboxInput";
import {SelectInput} from "../base/SelectInput";
import {LANGUAGES, MEASURE_SYSTEMS} from "../../utils/enumItemsUtils";
import {useLocalizedEnumItems} from "../../hooks/localizedEnumItemsHooks";
import {DateFormatType} from "../../types/domain/dateFormat";

export interface UserInterfaceFormInputs {
    language: LanguageType;
    measureSystem: MeasureSystemType;
    enabledUiHelp: boolean;
    enabledAdvancedFeatures: boolean;
    dateFormat: DateFormatType;
}

export interface Props {
    initFormValues: UserInterfaceFormInputs;
    form: UseFormReturn<UserInterfaceFormInputs>;
}

export const UserInterfaceForm: React.FC<Props> = ({initFormValues, form: {control}}) => {
    const {t} = useTranslation();
    const {items: measureSystemOptions} = useLocalizedEnumItems(MEASURE_SYSTEMS);
    const dateFormatOptions = [
        {
            id: DateFormatType.EU,
            label: t("enumerations.date_formats.eu"),
        },
        {
            id: DateFormatType.US,
            label: t("enumerations.date_formats.us"),
        },
    ];

    return (
        <Frame>
            <Typography fontSize={16} textTransform="uppercase" sx={{marginBottom: "1rem"}}>
                {t("users.interface_form.title")}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <LabeledInput label={t("users.interface_form.language")}>
                        <SelectInput
                            name="language"
                            control={control}
                            options={LANGUAGES}
                            initialValue={initFormValues.language}
                            getOptionLabel={(option) => option.label}
                        />
                    </LabeledInput>
                </Grid>
                <Grid item xs={6}>
                    <LabeledInput label={t("users.interface_form.unit")}>
                        <SelectInput
                            name="measureSystem"
                            control={control}
                            options={measureSystemOptions}
                            initialValue={initFormValues.measureSystem}
                            getOptionLabel={(option) => option.label}
                        />
                    </LabeledInput>
                </Grid>
                <Grid item xs={6}>
                    <LabeledInput label={t("users.interface_form.date_format")}>
                        <SelectInput
                            name="dateFormat"
                            control={control}
                            options={dateFormatOptions}
                            initialValue={initFormValues.dateFormat}
                            getOptionLabel={(option) => option.label}
                        />
                    </LabeledInput>
                </Grid>
                <Grid item xs={12}>
                    <CheckboxInput
                        name="enabledUiHelp"
                        control={control}
                        initialValue={initFormValues.enabledUiHelp}
                        label={t("users.interface_form.onscreen_help")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CheckboxInput
                        name="enabledAdvancedFeatures"
                        control={control}
                        initialValue={initFormValues.enabledAdvancedFeatures}
                        label={t("users.interface_form.advanced")}
                    />
                </Grid>
            </Grid>
        </Frame>
    );
};
